/**
 * Type Guards
 * Narrowing predicates for untrusted external data — imported files, API payloads, parsed JSON.
 *
 * These mirror the guards in `@nexus/core/utils` rather than importing them. That package's `./utils`
 * entry re-exports its pino logger, which pulls in `node:fs`, `node:path` and `node:async_hooks`, so
 * importing it would drag Node built-ins into the browser bundle. Keep the two in step if either moves.
 *
 * Each guard is copied verbatim from `libs/core/src/utils/type-guards.ts`, in the same order, but only
 * the ones the UI actually calls are here. Copy more across as they are needed rather than mirroring
 * the whole module — an unused guard is a browser-side copy of server semantics nothing checks.
 * @packageDocumentation
 */

/**
 * Checks if a value is a plain object (not null, not an array).
 *
 * @param { unknown } value The value to check.
 * @returns { boolean } True if the value is a plain object.
 * @example
 * isPlainObject({}) // true
 * isPlainObject({ a: 1 }) // true
 * isPlainObject([]) // false
 * isPlainObject(null) // false
 */
export const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Checks if a value is null or undefined.
 *
 * @param { unknown } value The value to check.
 * @returns { boolean } True if the value is null or undefined.
 * @example
 * isNullish(null) // true
 * isNullish(undefined) // true
 * isNullish(0) // false
 * isNullish('') // false
 */
export const isNullish = (value: unknown): value is null | undefined => value === null || value === undefined;

/**
 * Checks if a value is undefined.
 * Narrower than `isNullish`: an explicit null is not undefined, which matters where absent and
 * null mean different things — an omitted JSON field versus one set to null.
 *
 * @param { unknown } value The value to check.
 * @returns { boolean } True if the value is undefined.
 * @example
 * isUndefined(undefined) // true
 * isUndefined(null) // false (isNullish would be true)
 * isUndefined(0) // false
 * isUndefined('') // false
 */
export const isUndefined = (value: unknown): value is undefined => value === undefined;

/**
 * Checks if a value is a string.
 *
 * @param { unknown } value The value to check.
 * @returns { boolean } True if the value is a string.
 * @example
 * isString('hello') // true
 * isString(42) // false
 * isString(new String('hello')) // false (String object, not primitive)
 */
export const isString = (value: unknown): value is string => typeof value === 'string';

/**
 * Checks if a value is a boolean.
 *
 * @param { unknown } value The value to check.
 * @returns { boolean } True if the value is a boolean.
 * @example
 * isBoolean(true) // true
 * isBoolean(false) // true
 * isBoolean(0) // false
 * isBoolean('true') // false
 */
export const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';

/**
 * Checks if a value is empty.
 * Empty values include: null, undefined, empty string, empty array, plain empty object.
 * Note: Non-plain objects (Date, Map, etc.) are never considered empty.
 *
 * @param { unknown } value The value to check.
 * @returns { boolean } True if the value is empty.
 * @example
 * isEmpty(null) // true
 * isEmpty(undefined) // true
 * isEmpty('') // true
 * isEmpty([]) // true
 * isEmpty({}) // true
 * isEmpty(0) // false
 * isEmpty('hello') // false
 * isEmpty(new Date()) // false
 */
export const isEmpty = (value: unknown): boolean => {
  if (isNullish(value)) {
    return true;
  }

  if (typeof value === 'string') {
    return value.length === 0;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  // Only check true plain objects (constructor === Object), not class instances
  if (typeof value === 'object' && value !== null && value.constructor === Object) {
    return Object.keys(value).length === 0;
  }

  return false;
};
